import { Link, useLocation } from "react-router-dom";
import { useTrackedNavigate } from "@/hooks/useTrackedNavigate";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useProfile } from "@/hooks/useProfile";
import { useAuth } from "@/hooks/useAuth";
import { useOnboardingStatusWithSession } from "@/hooks/useOnboardingStatus";
import { useRoleMode } from "@/contexts/RoleModeContext";
import { useActiveCompany } from "@/contexts/ActiveCompanyContext";
import { User, Briefcase } from "lucide-react";
import { useFeatureEnabled } from "@/hooks/useFeatureEnabled";

export function UserMenu() {
  const location = useLocation();
  const navigate = useTrackedNavigate();
  const { data: user } = useCurrentUser();
  const { data: profile } = useProfile(user?.id);
  const { signOut } = useAuth();
  const { role } = useOnboardingStatusWithSession();
  const { mode, setMode } = useRoleMode();
  const { companies, activeCompany, setActiveCompany } = useActiveCompany();
  
  // Check if employer features are enabled
  const { isEnabled: employerModeEnabled } = useFeatureEnabled('feature_employer_mode_enabled');

  const getInitials = (name?: string | null) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const isActive = (path: string) => location.pathname === path;

  // Check if user has permission to access employer mode
  const hasEmployerAccess = role === "employer" || companies.length > 0;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="focus:outline-none min-h-[44px] min-w-[44px] inline-flex items-center justify-center" data-testid="user-menu-trigger">
        <Avatar className="h-10 w-10 cursor-pointer ring-2 ring-transparent hover:ring-primary/20 transition-all active:scale-95" data-testid="user-avatar">
          <AvatarImage src={profile?.avatar_url || ""} />
          <AvatarFallback className="bg-primary/10 text-primary text-sm font-medium">
            {getInitials(profile?.full_name)}
          </AvatarFallback>
        </Avatar>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{profile?.full_name || "User"}</p>
            <p className="text-xs leading-none text-muted-foreground">{user?.email}</p>
          </div>
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        {/* Role Switcher - Only show when employer mode is enabled */}
        {user && employerModeEnabled && (
          <>
            <div className="px-2 py-1.5 space-y-1">
              <Button
                variant={mode === "personal" ? "default" : "ghost"}
                size="sm"
                className="w-full justify-start"
                onClick={() => {
                  setMode("personal");
                  navigate("/dashboard");
                }}
                data-testid="user-menu-personal-mode"
              >
                <User className="h-4 w-4 mr-2" />
                Personal
              </Button>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="w-full">
                      <Button
                        variant={mode === "employer" ? "default" : "ghost"}
                        size="sm"
                        className="w-full justify-start"
                        disabled={false}
                        onClick={() => {
                          if (!hasEmployerAccess) {
                            navigate("/request-employer-access");
                            return;
                          }
                          setMode("employer");
                          // Auto-select first company if none active
                          if (companies.length > 0 && !activeCompany) {
                            setActiveCompany(companies[0]);
                          }
                          navigate("/ats");
                        }}
                        data-testid="user-menu-employer-mode"
                      >
                        <Briefcase className="h-4 w-4 mr-2" />
                        Employer
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {!hasEmployerAccess && (
                    <TooltipContent>
                      <div className="text-center space-y-1">
                        <p className="text-sm">
                          <Link to="/request-employer-access" className="text-primary hover:underline">
                            Request Employer Access
                          </Link>
                        </p>
                      </div>
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            </div>
            <DropdownMenuSeparator />
          </>
        )}

        {/* Navigation Links */}
        <DropdownMenuItem asChild>
          <Link
            to="/dashboard"
            className={isActive("/dashboard") ? "bg-accent" : ""}
            data-testid="nav-dashboard"
          >
            <span className="mr-2 text-base">🏠</span>
            Dashboard
          </Link>
        </DropdownMenuItem>

        <DropdownMenuItem asChild>
          <Link
            to="/people"
            className={isActive("/people") ? "bg-accent" : ""}
            data-testid="nav-people"
          >
            <span className="mr-2 text-base">👥</span>
            My People
          </Link>
        </DropdownMenuItem>

        <DropdownMenuItem asChild>
          <Link
            to="/finance"
            className={isActive("/finance") ? "bg-accent" : ""}
          >
            <span className="mr-2 text-base">💰</span>
            Finance
          </Link>
        </DropdownMenuItem>

        {/* Personal Mode Items */}
        {mode === "personal" && (
          <>
            <DropdownMenuItem asChild>
              <Link
                to="/my-profile"
                className={isActive("/my-profile") ? "bg-accent" : ""}
                data-testid="nav-my-profile"
              >
                <span className="mr-2 text-base">👤</span>
                Personal Profile
              </Link>
            </DropdownMenuItem>

            {/* Applications deactivated for personal-only MVP */}

            <DropdownMenuItem asChild>
              <Link
                to="/my-events"
                className={isActive("/my-events") ? "bg-accent" : ""}
              >
                <span className="mr-2 text-base">🗓️</span>
                My Events
              </Link>
            </DropdownMenuItem>

            <DropdownMenuItem asChild>
              <Link
                to="/my-documents"
                className={isActive("/my-documents") ? "bg-accent" : ""}
              >
                <span className="mr-2 text-base">📄</span>
                My Documents
              </Link>
            </DropdownMenuItem>
          </>
        )}

        {/* Employer Mode Items */}
        {mode === "employer" && (
          <>
            <DropdownMenuItem asChild>
              <Link
                to="/profile"
                className={isActive("/profile") ? "bg-accent" : ""}
              >
                <span className="mr-2 text-base">🏢</span>
                Company Profile
              </Link>
            </DropdownMenuItem>

            <DropdownMenuItem asChild>
              <Link
                to="/jobs"
                className={isActive("/jobs") ? "bg-accent" : ""}
              >
                <span className="mr-2 text-base">📋</span>
                Jobs
              </Link>
            </DropdownMenuItem>

            <DropdownMenuItem asChild>
              <Link
                to="/ats"
                className={isActive("/ats") ? "bg-accent" : ""}
              >
                <span className="mr-2 text-base">📊</span>
                ATS
              </Link>
            </DropdownMenuItem>

            <DropdownMenuItem asChild>
              <Link
                to="/interviews"
                className={isActive("/interviews") ? "bg-accent" : ""}
              >
                <span className="mr-2 text-base">📅</span>
                Interviews
              </Link>
            </DropdownMenuItem>
          </>
        )}

        {/* Shared Items - Only show when employer mode is enabled */}
        {employerModeEnabled && (
          <>
            <DropdownMenuItem asChild>
              <Link
                to="/companies"
                className={isActive("/companies") ? "bg-accent" : ""}
              >
                <span className="mr-2 text-base">🏢</span>
                Companies
              </Link>
            </DropdownMenuItem>

            <DropdownMenuItem asChild>
              <Link
                to="/marketplace"
                className={isActive("/marketplace") ? "bg-accent" : ""}
              >
                <span className="mr-2 text-base">🛍️</span>
                Marketplace
              </Link>
            </DropdownMenuItem>
          </>
        )}

        <DropdownMenuSeparator />

        <DropdownMenuItem asChild>
          <Link
            to="/settings/personal-info"
            className={location.pathname.startsWith("/settings") ? "bg-accent" : ""}
            data-testid="nav-settings"
          >
            <span className="mr-2 text-base">⚙️</span>
            Settings
          </Link>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem onClick={signOut} className="text-destructive focus:text-destructive" data-testid="signout-btn">
          <span className="mr-2 text-base">🚪</span>
          Sign Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
